/**
 * The browser call engine for /collect (a server-rendered page, so this is
 * bundled by vite into the fixed-name public/dist/collect-call.js and exposed
 * on window.__emberCall). It owns exactly what the vendor SDK is for —
 * transport and audio — and hands everything else back to the page as events.
 *
 * Two legs, the page's policy decides which (see collect-routes.js):
 *   webrtc — the SDK default. Ember's voice arrives as a WebRTC audio track
 *            played through an <audio> element and the microphone is a WebRTC
 *            track, so the browser's echo canceller has its reference signal
 *            (the raw-websocket client this replaced played through Web Audio,
 *            which some browsers do not cancel — a desktop caller on speakers
 *            fed Ember's every line back into the mic, 2026-09-08).
 *   relay  — the conversation websocket bridged through our host for networks
 *            that reset direct vendor connections (mainland China). The SDK's
 *            websocket path; its worklets are self-hosted below so the leg has
 *            no CDN dependency.
 *
 * Volume: the SDK plays the WebRTC track through an <audio> element whose
 * `volume` iOS refuses to script. iOS callers set the volume in a third of
 * calls, so on iOS the track is also fed through a Web Audio gain (the muted
 * element keeps the track flowing — WebKit only delivers a remote track to
 * Web Audio while a media element plays it). iOS cancels echo at the system
 * level across every output, so this path costs nothing there. Elsewhere the
 * element's own volume is set. Both sit behind the SDK's setVolume through the
 * adapter hook the SDK exposes for exactly this (setWebRTCAudioAdapterFactory).
 *
 * Speaking / listening on the WebRTC leg is read off the track's own level
 * (an analyser, 700 ms of silence = Ember finished) rather than LiveKit's
 * active-speaker events: those flip on every pause between sentences, which
 * read as "your turn" mid-line and stamped opening_completed two seconds in
 * (dev 2026-09-08). The same level marks the first audible audio.
 *
 * The AudioContext comes from the page, created inside the tap that starts the
 * call: iOS only lets a context run that a gesture unlocked, and the track
 * arrives seconds later from the network, outside any gesture.
 */
const SPEECH_RMS = 0.01;
const SILENCE_MS = 700;
import { Conversation, type VolumeProvider } from '@elevenlabs/client';
import {
  setWebRTCAudioAdapterFactory,
  type AnalysisResult,
  type WebRTCAudioAdapter,
} from '@elevenlabs/client/internal';
import rawAudioProcessorUrl from '@elevenlabs/client/worklets/rawAudioProcessor.js?url';
import audioConcatProcessorUrl from '@elevenlabs/client/worklets/audioConcatProcessor.js?url';
import libsamplerateUrl from '@alexanderolsen/libsamplerate-js/dist/libsamplerate.worklet.js?url';
import { createQuietClock, type QuietClock, type QuietClockOptions } from './quiet-clock';

type RemoteTrackLike = {
  attach: () => HTMLMediaElement;
  mediaStreamTrack: MediaStreamTrack;
};

type Initiation = {
  conversation_config_override?: {
    agent?: { first_message?: string; language?: string; prompt?: unknown };
    tts?: { voice_id?: string };
  };
  dynamic_variables?: Record<string, string | number | boolean>;
  custom_llm_extra_body?: unknown;
};

export type CallEvent =
  | { type: 'agent_response'; text: string }
  | { type: 'agent_response_correction'; text: string }
  | { type: 'user_transcript'; text: string }
  | { type: 'interruption' };

export type StartOptions = {
  leg: 'webrtc' | 'relay';
  conversationToken?: string;
  signedUrl?: string;
  initiation: Initiation;
  volume: number;
  /** Created by the page inside the user's tap; see the file comment. */
  audioContext?: AudioContext;
  on: {
    connected: (conversationId: string) => void;
    mode: (mode: 'speaking' | 'listening') => void;
    message: (event: CallEvent) => void;
    firstAudio: () => void;
    reconnecting: () => void;
    reconnected: () => void;
    playbackStalled: () => void;
    /**
     * The vendor's voice-activity score for the caller's microphone, 0–1,
     * many times a second (2026-10-21 Riley): the page's quiet clock counts
     * it as the caller speaking, so a long turn with pauses — which yields no
     * transcript until the vendor takes the turn — is no longer silence.
     * Only arrives when the agent's client events include vad_score.
     */
    vad: (score: number) => void;
    /**
     * closeReason: the vendor's own words for an 'agent' close when it gave
     * any (the websocket close reason, or LiveKit's disconnect reason on the
     * WebRTC leg) — the page reads the 20-minute cap off it.
     */
    disconnected: (details: { reason: 'user' | 'agent' | 'error'; message?: string; closeReason?: string }) => void;
    error: (message: string) => void;
  };
};

export type CallSession = {
  id: () => string;
  end: () => Promise<void>;
  setVolume: (volume: number) => void;
  resumePlayback: () => Promise<void>;
};

const NO_VOLUME: VolumeProvider = { getVolume: () => 0, getByteFrequencyData: () => {} };

function isIos(): boolean {
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

/** Playback of the agent's WebRTC track — see the file comment for the iOS branch. */
class PlaybackAdapter implements WebRTCAudioAdapter {
  private elements: HTMLMediaElement[] = [];
  private ctx: AudioContext | null;
  private readonly ownsCtx: boolean;
  private gain: GainNode | null = null;
  private levelTimer: ReturnType<typeof setInterval> | null = null;
  private closing = false;

  constructor(
    private readonly ios: boolean,
    private volume: number,
    audioContext: AudioContext | undefined,
    private readonly hooks: { stalled: () => void; level: (mode: 'speaking' | 'listening') => void },
  ) {
    this.ctx = audioContext || null;
    this.ownsCtx = !audioContext;
  }

  private context(): AudioContext {
    if (!this.ctx) this.ctx = new AudioContext();
    if (this.ctx.state !== 'running') this.ctx.resume().catch(() => {});
    return this.ctx;
  }

  /** Ember speaking = the track carries signal; finished = SILENCE_MS without it. */
  private watchLevel(source: MediaStreamAudioSourceNode, ctx: AudioContext): void {
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    source.connect(analyser);
    const samples = new Float32Array(analyser.fftSize);
    let speaking = false;
    let quietSince = 0;
    this.levelTimer = setInterval(() => {
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
      const rms = Math.sqrt(sum / samples.length);
      const now = Date.now();
      if (rms > SPEECH_RMS) {
        quietSince = 0;
        if (!speaking) { speaking = true; this.hooks.level('speaking'); }
      } else if (speaking) {
        if (!quietSince) quietSince = now;
        else if (now - quietSince >= SILENCE_MS) { speaking = false; this.hooks.level('listening'); }
      }
    }, 100);
  }

  async attachRemoteTrack(track: unknown, outputDeviceId: string | null): Promise<void> {
    const remote = track as RemoteTrackLike;
    const element = remote.attach();
    element.autoplay = true;
    element.controls = false;
    element.style.display = 'none';
    if (outputDeviceId && 'setSinkId' in element) {
      try { await (element as HTMLMediaElement & { setSinkId: (id: string) => Promise<void> }).setSinkId(outputDeviceId); } catch { /* keep default output */ }
    }
    element.addEventListener('pause', () => { if (!this.closing) this.hooks.stalled(); });
    const ctx = this.context();
    const source = ctx.createMediaStreamSource(new MediaStream([remote.mediaStreamTrack]));
    this.watchLevel(source, ctx);
    if (this.ios) {
      element.muted = true;
      const gain = ctx.createGain();
      gain.gain.value = this.volume;
      source.connect(gain);
      gain.connect(ctx.destination);
      this.gain = gain;
    } else {
      element.volume = this.volume;
    }
    document.body.appendChild(element);
    this.elements.push(element);
    try { await element.play(); } catch { this.hooks.stalled(); }
  }

  setupInputAnalysis(): AnalysisResult {
    return { volumeProvider: NO_VOLUME };
  }

  async setupOutputAnalysis(): Promise<AnalysisResult> {
    return { volumeProvider: NO_VOLUME };
  }

  setVolume(volume: number): void {
    this.volume = volume;
    if (this.gain && this.ctx) {
      this.gain.gain.cancelScheduledValues(this.ctx.currentTime);
      this.gain.gain.setTargetAtTime(volume, this.ctx.currentTime, 0.015);
      return;
    }
    for (const element of this.elements) element.volume = volume;
  }

  async setOutputDevice(deviceId: string): Promise<void> {
    if (!('setSinkId' in HTMLMediaElement.prototype)) throw new Error('setSinkId is not supported in this browser');
    await Promise.all(this.elements.map((element) => (
      (element as HTMLMediaElement & { setSinkId: (id: string) => Promise<void> }).setSinkId(deviceId)
    )));
  }

  async resume(): Promise<void> {
    if (this.ctx && this.ctx.state !== 'running') await this.ctx.resume();
    await Promise.all(this.elements.map((element) => element.play()));
  }

  cleanup(): void {
    this.closing = true;
    if (this.levelTimer) { clearInterval(this.levelTimer); this.levelTimer = null; }
    for (const element of this.elements) element.remove();
    this.elements = [];
    if (this.ctx && this.ownsCtx) this.ctx.close().catch(() => {});
    this.ctx = null;
    this.gain = null;
  }
}

function sessionOverrides(initiation: Initiation) {
  const override = initiation.conversation_config_override || {};
  return {
    agent: {
      firstMessage: override.agent?.first_message,
      language: override.agent?.language as never,
    },
    tts: { voiceId: override.tts?.voice_id },
  };
}

async function start(options: StartOptions): Promise<CallSession> {
  const { on } = options;
  let firstAudioReported = false;
  const firstAudio = () => {
    if (firstAudioReported) return;
    firstAudioReported = true;
    on.firstAudio();
  };
  const adapter = new PlaybackAdapter(isIos(), options.volume, options.audioContext, {
    stalled: () => on.playbackStalled(),
    level: (mode) => { if (mode === 'speaking') firstAudio(); on.mode(mode); },
  });
  setWebRTCAudioAdapterFactory(() => adapter);

  const leg = options.leg === 'relay'
    ? {
      connectionType: 'websocket' as const,
      signedUrl: options.signedUrl || '',
      workletPaths: { rawAudioProcessor: rawAudioProcessorUrl, audioConcatProcessor: audioConcatProcessorUrl },
      libsampleratePath: libsamplerateUrl,
    }
    : { connectionType: 'webrtc' as const, conversationToken: options.conversationToken || '' };

  const conversation = await Conversation.startSession({
    ...leg,
    overrides: sessionOverrides(options.initiation),
    dynamicVariables: options.initiation.dynamic_variables,
    customLlmExtraBody: options.initiation.custom_llm_extra_body,
    onConnect: ({ conversationId }) => on.connected(conversationId),
    // The WebRTC leg reads speaking/listening off the track level (adapter);
    // the websocket leg has the SDK's exact playback-queue mode.
    ...(options.leg === 'relay' ? { onModeChange: ({ mode }: { mode: 'speaking' | 'listening' }) => on.mode(mode) } : {}),
    onMessage: ({ source, message }) => on.message(
      source === 'ai' ? { type: 'agent_response', text: message } : { type: 'user_transcript', text: message },
    ),
    onAgentResponseCorrection: (event) => on.message({ type: 'agent_response_correction', text: event.corrected_agent_response }),
    onInterruption: () => on.message({ type: 'interruption' }),
    onVadScore: ({ vadScore }) => on.vad(Number(vadScore) || 0),
    ...(options.leg === 'relay' ? { onAudio: firstAudio } : {}),
    onDisconnect: (details) => on.disconnected({
      reason: details.reason,
      message: 'message' in details ? String(details.message || '') : undefined,
      closeReason: details.reason === 'user' ? undefined
        : String(details.closeReason || details.context?.reason || ''),
    }),
    onError: (message) => on.error(String(message || '')),
  });
  conversation.setVolume({ volume: options.volume });

  // LiveKit's own reconnection (an ICE restart or a signal reconnect on a
  // network blip) is the recovery layer here; the page only needs to say
  // "Reconnecting…" while it runs. Reached through the connection the SDK
  // keeps but does not export — guarded, so an SDK change costs the notice,
  // not the call.
  const room = (conversation as unknown as { connection?: { getRoom?: () => { on: (event: string, fn: () => void) => void } } })
    .connection?.getRoom?.();
  if (room) {
    room.on('reconnecting', () => on.reconnecting());
    room.on('reconnected', () => on.reconnected());
  }

  return {
    id: () => conversation.getId(),
    end: () => conversation.endSession(),
    setVolume: (volume) => conversation.setVolume({ volume }),
    resumePlayback: () => adapter.resume(),
  };
}

declare global {
  interface Window {
    __emberCall?: {
      start: (options: StartOptions) => Promise<CallSession>;
      /** The page's quiet clock (quiet-clock.ts): one per call, read on the live timer's tick. */
      quietClock: (options?: QuietClockOptions) => QuietClock;
    };
  }
}

window.__emberCall = { start, quietClock: createQuietClock };
window.dispatchEvent(new Event('ember-call-ready'));
